import { IPrismicLink, PrismicLinkContext } from '@monofront/types';
import Link from 'next/link';
import { linkResolver } from '@monofront/business/utils';
import React, { type JSX } from 'react';
import { FilledContentRelationshipField } from '@prismicio/client';

interface IPrismicLinkProps {
  link?: IPrismicLink;
  context: PrismicLinkContext;
  className?: string;
  trackingId?: string;
  children: React.ReactNode;
  style?: React.CSSProperties;
  onClick?: () => void;
  openSidePanel?: (uid?: string) => void;
}

const PrismicLink = ({
  link,
  context,
  children,
  className,
  trackingId,
  style,
  onClick,
  openSidePanel,
}: IPrismicLinkProps): JSX.Element => {
  if (!link || link.link_type === 'Any') {
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <>{children}</>;
  }

  const isWebUrl = link.link_type === 'Web' && 'url' in link && link?.url;
  const isRelativeWebUrl = !!isWebUrl && link.url.startsWith('/');

  const splittedUrl = isRelativeWebUrl
    ? link.url.split('/')
    : isWebUrl
    ? new URL(link.url).pathname.split('/')
    : [];

  if (
    link.link_type === 'Web' &&
    context === PrismicLinkContext.SHOP &&
    splittedUrl.length &&
    splittedUrl.some((path) => path === 'collections' || path === 'products')
  ) {
    return (
      <Link
        href={`/${splittedUrl[1]}/${splittedUrl[2]}${
          trackingId ? `?trackingId=${trackingId}` : ''
        }`}
        style={style}
        className={className}
        onClick={onClick}
      >
        {children}
      </Link>
    );
  }

  if (
    link.link_type === 'Web' ||
    (link.link_type === 'Document' &&
      'type' in link &&
      ['blogpost', 'blogcategory', 'bloghome'].includes(link.type))
  ) {
    const target =
      link.link_type === 'Web' && 'target' in link ? link.target : '_self';

    return (
      <a
        href={
          link.link_type === 'Web' && 'url' in link
            ? link.url
            : linkResolver(
                link as FilledContentRelationshipField,
                context,
                trackingId,
              )
        }
        target={target || '_self'}
        className={className}
        style={style}
        onClick={onClick}
      >
        {children}
      </a>
    );
  }

  if ((link as FilledContentRelationshipField).type === 'side_panel') {
    return (
      <button
        type="button"
        className={className}
        onClick={() =>
          openSidePanel?.((link as FilledContentRelationshipField).uid)
        }
      >
        {children}
      </button>
    );
  }

  return (
    <Link
      href={linkResolver(
        link as FilledContentRelationshipField,
        context,
        trackingId,
      )}
      className={className}
      style={style}
      onClick={onClick}
    >
      {children}
    </Link>
  );
};

export default PrismicLink;
